import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useParams } from "react-router-dom";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyQuery";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	PortfolioDetailKind,
	PortfolioDetailMenu,
} from "web_myplann/Portfolio/Components/PortfolioDetailMenu";
import { PropertySection } from "web_myplann/Portfolio/Components/PropertySections/PropertySection";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePropertyTitle } from "web_myplann/Portfolio/Hooks/usePropertyTitle";
import { StyledCenteredStack } from "web_myplann/Shared/Components/StyledCenteredStack";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const PropertyDetailPage: FC = () => {
	const routeParams = useParams<{ id: string }>();
	const propertyId = getTypedParam(routeParams, "id", Number);
	const { t } = useResource();
	const theme = useTheme();

	const { data: property, isLoading } = usePropertyQuery(propertyId);
	const hasPortfolioPilotRight = usePortfolioRightOptional();

	const title = usePropertyTitle(property, t);

	const currencySign = t(Resources.Common.CurrencySign);

	const showEmpty = !isLoading && hasNoBusinessData(property);

	const showDetailMenu =
		hasPortfolioPilotRight &&
		property?.type !== undefined &&
		propertyId !== undefined;

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={title}
					defaultRoute={Routing.Portfolio.PortfolioList.path()}
					alwaysUseDefaultRoute
					endComponent={
						showDetailMenu ? (
							<PortfolioDetailMenu
								kind={PortfolioDetailKind.Property}
								id={propertyId}
								propertyType={property.type}
								title={title}
							/>
						) : undefined
					}
				/>
			}
			disableBottomPadding
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<StyledCenteredStack spacing={2} px={5}>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<Stack textAlign="center" gap={0.4}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.empty)}
						</Typography>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
						</Typography>
					</Stack>
				</StyledCenteredStack>
			) : (
				<Stack pt={2}>
					{property && propertyId !== undefined && (
						<PropertySection
							property={property}
							currencySign={currencySign}
							documentsId={propertyId}
						/>
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
